import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  Button,
  Snackbar,
  Alert,
  Slide,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Hotel as HotelIcon,
  Payment as PaymentIcon,
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon,
  RateReview as RateReviewIcon,
  FactCheck as FactCheckIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Login as CheckInIcon,
  Logout as CheckOutIcon,
  Room as RoomIcon,
  Timeline as TimelineIcon,
  Event as EventIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  ConnectingAirports as ConnectingAirportsIcon,
  MeetingRoom as MeetingRoomIcon,
  Monitor as MonitorIcon,
  DirectionsWalk as DirectionsWalkIcon,
  Groups as GroupsIcon,
  BusinessCenter as BusinessCenterIcon,
  SupportAgent as SupportAgentIcon,
  Api as ApiIcon,
  HelpOutline as HelpOutlineIcon,
  PersonOutline as PersonOutlineIcon,
  Restaurant as RestaurantIcon,
  FlagOutlined as FlagOutlinedIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import DashboardHeader from '../components/common/DashboardHeader';
import PageBackNav from '../components/common/PageBackNav';
import useMobileView from '../hooks/useMobileView';
import BookingPaymentLinkPanel from '../components/booking/BookingPaymentLinkPanel';
import BookingOverpaidBanner from '../components/booking/BookingOverpaidBanner';
import BookingDocumentsPreviewPanel from '../components/booking/BookingDocumentsPreviewPanel';
import ChannelImage from '../components/common/ChannelImage';
import CustomerContactInfoDrawer from '../components/customer/CustomerContactInfoDrawer';
import useStayGuestDrawer from '../hooks/useStayGuestDrawer';
import { sanitizeBookingAuditActor, sanitizeBookingAuditText } from '../utils/bookingAuditLayout';
import {
  getBookingDetails,
  getBookingAuditEvents,
  getHotelCommunicationSettings,
  isCommEventEnabled,
  sendBookingReviewRequest,
  updateBookingStayGuest,
} from '../services/api';
import {
  formatWhatsAppErrorMessage,
  formatWhatsAppSuccessMessage,
} from '../utils/whatsappMessaging';
import { resolveBookingInvoiceNumber } from '../utils/bookingInvoiceNumber';
import { getPaymentSummaryData } from '../utils/bookingPaymentSummary';
import BookingExtraChargesBreakdown from '../components/booking/BookingExtraChargesBreakdown';
import moment from 'moment';
import {
  resolveActualCheckInDisplay,
  resolveActualCheckOutDisplay,
  getStayDurationMeta,
  isSameDayStay,
} from '../utils/bookingStayDisplay';
import { THEME_COLORS } from '../shared/constants/theme';
import {
  DASHBOARD_ACCENT,
  getDashboardDarkSurfaceTokens,
  getDashboardPageShellSx,
} from '../utils/dashboardSurfaceSx';
import { canModifyBooking, hasFeaturePermission, PERMISSIONS } from '../utils/featurePermissions';
import { getPermissionDeniedMessage, PERMISSION_DENIED_MESSAGE } from '../auth/permissions';
import { clearCachedBookingGuestDocuments } from '../utils/bookingGuestDocumentsCache';

function SlideLeftTransition(props) {
  return <Slide {...props} direction="left" />;
}

function formatBookingGuestName(g) {
  if (!g) return 'Guest';
  const fromParts = [g.title, g.first_name, g.middle_name, g.last_name].filter(Boolean).join(' ').trim();
  if (fromParts) return fromParts;
  if (typeof g.name === 'string' && g.name.trim()) return g.name.trim();
  return 'Guest';
}

/** Comma-separated guest names for a booking item row. */
function formatBookingItemGuestNames(item) {
  const guests = Array.isArray(item?.guests) && item.guests.length > 0
    ? item.guests
    : (item?.guest ? [item.guest] : []);
  if (!guests.length) return '—';
  const seen = new Set();
  const names = [];
  guests.forEach((g) => {
    if (!g) return;
    const key = g.id != null ? `id:${g.id}` : formatBookingGuestName(g).toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(formatBookingGuestName(g));
  });
  return names.length ? names.join(', ') : '—';
}

function formatPurposeOfStayLabel(purpose, display, note) {
  const fromDisplay = String(display || '').trim();
  if (fromDisplay && fromDisplay !== '—') {
    const noteText = String(note || '').trim();
    const code = String(purpose || '').trim();
    if (code === 'other' && noteText && !fromDisplay.toLowerCase().includes(noteText.toLowerCase())) {
      return `${fromDisplay} — ${noteText}`;
    }
    return fromDisplay;
  }
  const code = String(purpose || '').trim();
  if (!code) return '';
  const labels = {
    business: 'Business',
    leisure: 'Leisure',
    medical: 'Medical',
    family_visit: 'Family Visit',
    event: 'Event',
    transit: 'Transit',
    education: 'Education',
    government: 'Government',
    other: 'Other',
  };
  const base = labels[code] || code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const noteText = String(note || '').trim();
  if (code === 'other' && noteText) return `${base} — ${noteText}`;
  return base;
}

const BOOKING_SOURCE_LABELS = {
  direct: 'Direct (Roomnexa)',
  booking_engine: 'Booking Engine (NexaStay)',
  ota: 'OTA (Online Travel Agency)',
  walk_in: 'Walk-In',
  corporate: 'Corporate',
  group: 'Group Booking',
  agent: 'Travel Agent',
  api: 'API Integration',
  other: 'Other',
};

function formatBookingSourceLabel(raw) {
  const k = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (!k) return '—';
  if (BOOKING_SOURCE_LABELS[k]) return BOOKING_SOURCE_LABELS[k];
  return k
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const BOOKING_TYPE_LABELS = {
  individual: 'Individual',
  group: 'Group',
  corporate: 'Corporate',
  walk_in: 'Walk-in',
  web_checkin: 'Web Check-in',
};

const INVOICE_SCOPE_OPTIONS = [
  { value: 'hotel', label: 'Hotel' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'combined', label: 'Combined' },
];

function formatBookingTypeLabel(raw, displayFromApi) {
  if (displayFromApi && String(displayFromApi).trim() && displayFromApi !== '—') {
    return String(displayFromApi).trim();
  }
  const k = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (!k) return '—';
  if (BOOKING_TYPE_LABELS[k]) return BOOKING_TYPE_LABELS[k];
  return k
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function normalizeBookingSourceKey(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

function bookingSourceIconAndColor(sourceRaw) {
  const src = normalizeBookingSourceKey(sourceRaw);
  if (src === 'ota') return { Icon: ConnectingAirportsIcon, color: '#2563eb' };
  if (src === 'booking_engine') return { Icon: MeetingRoomIcon, color: '#14b8a6' };
  if (src === 'direct') return { Icon: MonitorIcon, color: '#6366f1' };
  if (src === 'walk_in') return { Icon: DirectionsWalkIcon, color: '#8b5cf6' };
  if (src === 'group') return { Icon: GroupsIcon, color: '#ca8a04' };
  if (src === 'corporate') return { Icon: BusinessCenterIcon, color: '#0d9488' };
  if (src === 'agent') return { Icon: SupportAgentIcon, color: '#db2777' };
  if (src === 'api') return { Icon: ApiIcon, color: '#64748b' };
  return { Icon: HelpOutlineIcon, color: '#64748b' };
}

function bookingTypeIconAndColor(typeRaw) {
  const t = String(typeRaw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (t === 'individual') return { Icon: PersonOutlineIcon, color: '#2563eb' };
  if (t === 'group') return { Icon: GroupsIcon, color: '#ca8a04' };
  if (t === 'corporate') return { Icon: BusinessCenterIcon, color: '#0d9488' };
  if (t === 'walk_in') return { Icon: DirectionsWalkIcon, color: '#9333ea' };
  return { Icon: HelpOutlineIcon, color: '#64748b' };
}

function BookingSummaryDetailRow({ icon: Icon, label, children, isDark, accent, iconColor }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        py: { xs: 1.1, sm: 0.85 },
        borderBottom: `1px solid ${isDark ? alpha('#fff', 0.06) : alpha('#0f172a', 0.06)}`,
        '&:last-of-type': { borderBottom: 'none', pb: 0 },
      }}
    >
      <Icon sx={{ fontSize: 20, color: iconColor || accent, mt: 0.15, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: isDark ? '#94a3b8' : '#64748b',
            fontWeight: 700,
            mb: 0.35,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            fontSize: '0.68rem',
          }}
        >
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, minWidth: 0 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

const BookingDetailPage = () => {
  const { bookingId } = useParams();
  const normalizedBookingId = useMemo(() => {
    const parsedId = Number(bookingId);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  }, [bookingId]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { isCompact } = useMobileView();
  const canEditBooking = canModifyBooking();
  const canViewBookingAudit = hasFeaturePermission(PERMISSIONS.BOOKING_AUDIT_VIEW);
  const canViewPosAudit = hasFeaturePermission(PERMISSIONS.POS_AUDIT_VIEW);

  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [docLoading, setDocLoading] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [auditExpanded, setAuditExpanded] = useState(false);
  const [restaurantAuditExpanded, setRestaurantAuditExpanded] = useState(false);
  const [auditLoaded, setAuditLoaded] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  const [invoiceScope, setInvoiceScope] = useState('hotel');
  const [commSettings, setCommSettings] = useState(null);
  const stayGuestDrawer = useStayGuestDrawer();
  const [stayGuestSaving, setStayGuestSaving] = useState(false);

  const canSendInvoiceEmail = useMemo(
    () => isCommEventEnabled(commSettings, 'invoice_voucher', 'email'),
    [commSettings]
  );
  const canSendInvoiceWhatsApp = useMemo(
    () => isCommEventEnabled(commSettings, 'invoice_voucher', 'whatsapp'),
    [commSettings]
  );
  const canSendReviewEmail = useMemo(
    () => isCommEventEnabled(commSettings, 'review_guest', 'email'),
    [commSettings]
  );
  const canSendReviewWhatsApp = useMemo(
    () => isCommEventEnabled(commSettings, 'review_guest', 'whatsapp'),
    [commSettings]
  );
  const canSendPaymentLinkEmail = useMemo(
    () => isCommEventEnabled(commSettings, 'payment_link', 'email'),
    [commSettings]
  );
  const canSendPaymentLinkWhatsApp = useMemo(
    () => isCommEventEnabled(commSettings, 'payment_link', 'whatsapp'),
    [commSettings]
  );
  const bookingItems = useMemo(() => {
    const items = booking?.items ?? booking?.booking_items;
    return Array.isArray(items) ? items : [];
  }, [booking]);
  const restaurantOrders = useMemo(() => {
    const payload = booking?.restaurant_orders_summary;
    if (!payload?.has_orders) return { hasOrders: false, orders: [], items: [], summary: {} };
    return {
      hasOrders: true,
      orders: Array.isArray(payload.orders) ? payload.orders : [],
      items: Array.isArray(payload.items) ? payload.items : [],
      auditEvents: Array.isArray(payload.audit_events) ? payload.audit_events : [],
      summary: payload.summary || {},
    };
  }, [booking]);
  const restaurantOrderById = useMemo(() => {
    const map = new Map();
    restaurantOrders.orders.forEach((order) => {
      map.set(order.id, order);
    });
    return map;
  }, [restaurantOrders.orders]);

  useEffect(() => {
    getHotelCommunicationSettings()
      .then(setCommSettings)
      .catch(() => setCommSettings(null));
  }, []);

  useEffect(() => {
    const fetchBookingDetail = async () => {
      if (!normalizedBookingId) {
        setError('Invalid booking ID');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const data = await getBookingDetails(normalizedBookingId);
        setBooking(data);
        setAuditExpanded(false);
        setRestaurantAuditExpanded(false);
        setAuditLoaded(false);
        setAuditError(null);
        setAuditEvents([]);
        if (canViewBookingAudit) {
          try {
            const auditData = await getBookingAuditEvents(normalizedBookingId, { order: 'asc' });
            setAuditEvents(Array.isArray(auditData?.events) ? auditData.events : []);
            setAuditLoaded(true);
          } catch (auditErr) {
            setAuditLoaded(false);
            setAuditError(auditErr?.message || 'Failed to load booking audit events');
          }
        }
      } catch (err) {
        setError(err.message || 'Failed to load booking details');
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetail();
  }, [normalizedBookingId]);

  const refetchBooking = useCallback(async () => {
    if (!normalizedBookingId) return;
    try {
      const data = await getBookingDetails(normalizedBookingId);
      setBooking(data);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to refresh booking',
        severity: 'error',
      });
    }
  }, [normalizedBookingId]);

  const saveStayGuestFromDrawer = useCallback(
    async (payload) => {
      const bid = stayGuestDrawer.bookingId || booking?.id;
      const gid = stayGuestDrawer.guest?.id;
      if (!bid || !gid) return;
      setStayGuestSaving(true);
      try {
        const data = await updateBookingStayGuest(bid, { id: gid, ...payload });
        clearCachedBookingGuestDocuments(bid, gid);
        if (data && !data.error) {
          setBooking(data);
        } else {
          await refetchBooking();
        }
        stayGuestDrawer.close();
        setSnackbar({
          open: true,
          message: 'Stay guest info updated for this booking.',
          severity: 'success',
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err?.response?.data?.error || err?.message || 'Failed to update stay guest info.',
          severity: 'error',
        });
        throw err;
      } finally {
        setStayGuestSaving(false);
      }
    },
    [
      booking?.id,
      refetchBooking,
      stayGuestDrawer.bookingId,
      stayGuestDrawer.close,
      stayGuestDrawer.guest?.id,
    ]
  );

  const paymentLinkNotice = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const round2 = (x) => Math.round(Number(x) * 100) / 100;
  const formatCurrency = (amount, currency = 'INR') => {
    const val = round2(amount ?? 0);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return moment(dateString).format('DD MMM YYYY');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return moment(dateString).format('DD MMM YYYY, hh:mm A');
  };

  const scheduledCheckInLabel = booking?.check_in_date ? formatDate(booking.check_in_date) : '—';
  const scheduledCheckOutLabel = booking?.check_out_date ? formatDate(booking.check_out_date) : '—';
  const isMigratedBooking =
    String(booking?.code || '').toUpperCase().startsWith('MG-')
    || String(booking?.internal_notes || '').includes('Migrated booking');
  const actualCheckInDisplay = useMemo(() => {
    if (isMigratedBooking) {
      return booking?.check_in_date
        ? { date: formatDate(booking.check_in_date), time: null }
        : null;
    }
    return resolveActualCheckInDisplay(booking);
  }, [booking, isMigratedBooking]);
  const actualCheckOutDisplay = useMemo(() => {
    if (isMigratedBooking) {
      return booking?.check_out_date
        ? { date: formatDate(booking.check_out_date), time: null }
        : null;
    }
    return resolveActualCheckOutDisplay(booking);
  }, [booking, isMigratedBooking]);
  const stayDurationMeta = useMemo(() => getStayDurationMeta(booking), [booking]);
  const sameDayStay = useMemo(() => isSameDayStay(booking), [booking]);

  const getStatusColor = (status) => {
    const statusLower = String(status || '')
      .toLowerCase()
      .replace(/_/g, ' ');
    if (statusLower.includes('confirmed')) return 'success';
    if (statusLower.includes('checked in')) return 'info';
    if (statusLower.includes('checked out')) return 'default';
    if (statusLower.includes('cancelled')) return 'error';
    if (statusLower.includes('pending')) return 'warning';
    return 'default';
  };

  const formatStatusLabel = (status) => {
    const fromDisplay = String(booking?.status_display || '').trim();
    if (fromDisplay && fromDisplay !== '—') return fromDisplay;
    return String(status || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const fetchBookingAuditEventsData = async () => {
    if (!normalizedBookingId) return;
    if (!canViewBookingAudit) {
      setAuditError(PERMISSION_DENIED_MESSAGE);
      return;
    }
    setAuditLoading(true);
    setAuditError(null);
    try {
      const data = await getBookingAuditEvents(normalizedBookingId, { order: 'asc' });
      setAuditEvents(Array.isArray(data?.events) ? data.events : []);
      setAuditLoaded(true);
    } catch (err) {
      setAuditEvents([]);
      setAuditError(err?.message || 'Failed to load booking audit events');
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (auditExpanded && !auditLoaded && !auditLoading) {
      fetchBookingAuditEventsData();
    }
  }, [auditExpanded, auditLoaded, auditLoading, normalizedBookingId]);

  const getAuditEventMeta = (eventType) => {
    const normalized = String(eventType || '').toLowerCase();
    if (['created', 'confirmed'].includes(normalized)) return { icon: <CheckCircleIcon sx={{ fontSize: 14 }} />, color: '#4caf50' };
    if (['checked_in', 'partial_checkin'].includes(normalized)) return { icon: <CheckInIcon sx={{ fontSize: 14 }} />, color: '#2e7d32' };
    if (['checked_out', 'partial_checkout'].includes(normalized)) return { icon: <CheckOutIcon sx={{ fontSize: 14 }} />, color: '#6d6d6d' };
    if (['room_changed', 'room_upgraded', 'room_added', 'rooms_allocated'].includes(normalized)) return { icon: <RoomIcon sx={{ fontSize: 14 }} />, color: '#1976d2' };
    if (['guest_added', 'guest_updated', 'guest_removed', 'guest_rematched'].includes(normalized)) return { icon: <PersonIcon sx={{ fontSize: 14 }} />, color: '#6366f1' };
    if (['checkout_date_extended', 'checkout_date_shortened', 'booking_modified'].includes(normalized)) return { icon: <TimelineIcon sx={{ fontSize: 14 }} />, color: '#7b1fa2' };
    if (normalized === 'cancelled') return { icon: <CancelIcon sx={{ fontSize: 14 }} />, color: '#d32f2f' };
    if ([
      'payment_added',
      'payment_updated',
      'payment_deleted',
      'extra_charges_added',
      'extra_charges_updated',
      'extra_charges_removed',
    ].includes(normalized)) {
      return { icon: <EventIcon sx={{ fontSize: 14 }} />, color: '#f59e0b' };
    }
    return { icon: <EventIcon sx={{ fontSize: 14 }} />, color: accent };
  };

  // Keep all financial sections aligned by deriving totals from booking items first.
  const paymentSummaryValues = useMemo(() => {
    if (!booking) {
      return {
        subtotalBefore: 0,
        discountPct: 0,
        discountAmount: 0,
        subtotalAfter: 0,
        gstPct: 0,
        gstAmount: 0,
        totalDisplay: 0,
        totalPaidRaw: 0,
        totalPaid: 0,
        remainingRaw: 0,
        remainingComputed: 0,
        returnAmountComputed: 0,
        inclusiveDisplay: false,
        afterDiscountLabel: 'After Discount',
        gstPrefix: '',
        extraChargesAmount: 0,
        extraChargesBaseAmount: 0,
        extraChargesGstAmount: 0,
        extraChargesNote: '',
        extraChargesItems: [],
      };
    }

    const summary = getPaymentSummaryData(booking);
    const totalDisplay = summary.displayTotal ?? summary.totalAmount;
    const totalPaidRaw = summary.paidAmount;

    return {
      subtotalBefore: summary.displaySubtotal ?? summary.subtotalBeforeDiscount,
      discountPct: Number(summary.discountPercentage) || 0,
      discountAmount: summary.discountAmount,
      subtotalAfter: summary.displayAfterDiscount ?? summary.subtotalAfterDiscount,
      gstPct: Number(summary.gstPercentage) || 0,
      gstAmount: summary.gstAmount,
      totalDisplay,
      totalPaidRaw,
      totalPaid: summary.paidAmount,
      remainingRaw: summary.remainingAmount,
      remainingComputed: summary.remainingAmount,
      returnAmountComputed: round2(Math.max(0, totalPaidRaw - totalDisplay)),
      inclusiveDisplay: summary.inclusiveDisplay,
      afterDiscountLabel: summary.afterDiscountLabel || 'After Discount',
      gstPrefix: summary.gstPrefix || '',
      extraChargesAmount: summary.extraChargesAmount,
      extraChargesBaseAmount: summary.extraChargesBaseAmount,
      extraChargesGstAmount: summary.extraChargesGstAmount,
      extraChargesNote: summary.extraChargesNote,
      extraChargesItems: summary.extraChargesItems,
    };
  }, [booking]);

  const computedTotal = paymentSummaryValues.totalDisplay;
  const combinedPaymentSummary = useMemo(() => {
    const rest = restaurantOrders.summary || {};
    const restaurantTotal = round2(rest.total_amount || 0);
    const restaurantPaid = round2(rest.paid_amount || 0);
    const restaurantRemaining = round2(rest.remaining_amount || Math.max(0, restaurantTotal - restaurantPaid));
    const combinedTotal = round2(paymentSummaryValues.totalDisplay + restaurantTotal);
    const combinedPaid = round2(paymentSummaryValues.totalPaid + restaurantPaid);
    const combinedRemaining = round2(Math.max(0, paymentSummaryValues.remainingComputed + restaurantRemaining));
    return {
      restaurantTotal,
      restaurantPaid,
      restaurantRemaining,
      combinedTotal,
      combinedPaid,
      combinedRemaining,
    };
  }, [paymentSummaryValues, restaurantOrders]);

  // Effective payment status for display: treat remaining < 0.9 as paid
  const effectivePaymentStatus = useMemo(() => {
    if (!booking?.payment_summary) return null;
    const { totalPaidRaw, remainingRaw } = paymentSummaryValues;
    if (remainingRaw < 0.9) return 'paid';
    if (totalPaidRaw > 0) return 'partial';
    return 'unpaid';
  }, [booking?.payment_summary, paymentSummaryValues]);

  const guestsFromItems = useMemo(() => {
    const map = new Map();
    bookingItems.forEach((item) => {
      (item.guests || []).forEach((g) => {
        if (g && g.id != null && !map.has(g.id)) {
          map.set(g.id, g);
        }
      });
    });
    return Array.from(map.values());
  }, [bookingItems]);

  const displayPrimaryGuest = useMemo(() => {
    if (booking?.guest) return booking.guest;
    if (!guestsFromItems.length) return null;
    return guestsFromItems.find((g) => g.is_primary) || guestsFromItems[0];
  }, [booking?.guest, guestsFromItems]);

  const additionalBookingGuests = useMemo(() => {
    const pid = displayPrimaryGuest?.id;
    if (pid == null) return [];
    return guestsFromItems.filter((g) => g.id !== pid);
  }, [guestsFromItems, displayPrimaryGuest]);

  const primaryGuestDisplayName = useMemo(
    () => formatBookingGuestName(displayPrimaryGuest),
    [displayPrimaryGuest]
  );

  const bookingInvoiceNumber = useMemo(
    () => (booking ? resolveBookingInvoiceNumber(booking, booking?.payments) : null),
    [booking]
  );

  const themeMode = isDark ? 'dark' : 'light';
  const pageShellSx = getDashboardPageShellSx(themeMode);
  const surfaceTokens = getDashboardDarkSurfaceTokens(isDark);

  if (loading) {
    return (
      <Box sx={{ ...pageShellSx, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: DASHBOARD_ACCENT }} />
      </Box>
    );
  }

  if (error || !booking) {
    return (
      <Box sx={pageShellSx}>
        <DashboardHeader
          title="Booking Detail"
          backFallbackPath="/booking/list/"
        />
        <Paper
          sx={{
            p: 3,
            textAlign: 'center',
            borderRadius: 2,
            border: `1px solid ${surfaceTokens.surfaceBorder}`,
            background: isDark ? surfaceTokens.surfaceGradient : '#ffffff',
          }}
        >
          <Typography color="error">{error || 'Booking not found'}</Typography>
        </Paper>
      </Box>
    );
  }

  const accent = DASHBOARD_ACCENT;
  const bookingSpecialRequests = String(booking.special_requests || '').trim();
  const bookingGuestNotes = String(booking.guest_notes || '').trim();
  const bookingStaffNotes = String(booking.staff_notes || '').trim();
  const bookingInternalNotes = String(booking.internal_notes || '').trim();
  const notesAndRequests = [
    {
      key: 'special_requests',
      title: 'Special requests',
      value: bookingSpecialRequests,
      Icon: FactCheckIcon,
      color: '#d97706',
    },
    {
      key: 'guest_notes',
      title: 'Guest notes',
      value: bookingGuestNotes,
      Icon: PersonIcon,
      color: '#16a34a',
    },
    {
      key: 'staff_notes',
      title: 'Staff notes',
      value: bookingStaffNotes,
      Icon: SupportAgentIcon,
      color: '#ca8a04',
    },
    {
      key: 'internal_notes',
      title: 'Internal notes',
      value: bookingInternalNotes,
      Icon: EditIcon,
      color: '#7c3aed',
    },
  ].filter((item) => Boolean(item.value));
  const hasNotesAndRequests = notesAndRequests.length > 0;
  const modernPanelCardSx = {
    mb: 2,
    borderRadius: 2,
    border: `1px solid ${surfaceTokens.surfaceBorder}`,
    background: isDark ? surfaceTokens.surfaceGradient : '#ffffff',
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    overflow: 'hidden',
  };
  const modernPanelHeaderSx = {
    p: { xs: 1.5, sm: 1.75 },
    borderBottom: `1px solid ${surfaceTokens.surfaceBorder}`,
    background: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#000', 0.02),
  };
  const innerPanelSx = {
    borderRadius: 2,
    border: `1px solid ${surfaceTokens.surfaceBorder}`,
    background: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#000', 0.02),
  };
  const financePanelSx = (color = accent, filled = false) => ({
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    p: { xs: 1.2, sm: 1.35 },
    borderRadius: 2,
    border: `1px solid ${isDark ? alpha(color, 0.32) : alpha(color, 0.18)}`,
    background: filled
      ? (isDark ? `linear-gradient(135deg, ${alpha(color, 0.18)}, ${alpha(color, 0.08)})` : `linear-gradient(135deg, ${alpha(color, 0.11)}, ${alpha(color, 0.04)})`)
      : (isDark ? alpha(color, 0.08) : alpha(color, 0.035)),
  });
  const financeRowSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1.5,
    mb: 0.75,
  };
  const financeSectionTitleSx = (color = accent) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    mb: 1,
    color,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  });

  return (
    <Box sx={{ ...pageShellSx, width: '100%', boxSizing: 'border-box' }}>
      {isCompact ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            mb: 1.5,
            pb: 1.25,
            borderBottom: `1px solid ${surfaceTokens.surfaceBorder}`,
          }}
        >
          <PageBackNav
            label="Back"
            fallbackPath="/booking/list/"
            isDark={isDark}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: accent,
                fontWeight: 700,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {booking.code}
            </Typography>
          </Box>
          <Chip
            label={formatStatusLabel(booking.status)}
            color={getStatusColor(booking.status)}
            size="small"
            sx={{
              flexShrink: 0,
              fontWeight: 700,
              textTransform: 'capitalize',
              maxWidth: '42%',
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        </Stack>
      ) : (
        <DashboardHeader
          title="Booking Detail"
          backFallbackPath="/booking/list/"
        />
      )}

      {/* Hero Card */}
      <Paper
        elevation={0}
        sx={{
          ...modernPanelCardSx,
          mb: { xs: 2, sm: 3 },
          overflow: 'visible',
        }}
      >
        <Box sx={{ p: { xs: 1.5, sm: 2.1 } }}>
          <Stack spacing={{ xs: 1.75, sm: 0 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1.5, sm: 3 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    width: { xs: 52, sm: 72 },
                    height: { xs: 52, sm: 72 },
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    background: isDark
                      ? `linear-gradient(135deg, ${alpha(accent, 0.18)} 0%, ${alpha(accent, 0.08)} 100%)`
                      : `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha(accent, 0.06)} 100%)`,
                    boxShadow: isDark
                      ? `0 8px 18px ${alpha('#000', 0.28)}`
                      : `0 8px 18px ${alpha(accent, 0.14)}`,
                    border: `2px solid ${alpha(accent, isDark ? 0.28 : 0.35)}`,
                  }}
                >
                  <ReceiptIcon sx={{ fontSize: { xs: 26, sm: 34 }, color: accent }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {!isCompact && (
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: isDark ? '#e2e8f0' : '#0f172a',
                        mb: 0.5,
                        wordBreak: 'break-word',
                      }}
                    >
                      {booking.code}
                    </Typography>
                  )}
                  {booking.group_booking_code && booking.group_booking_id ? (
                    <Chip
                      size="small"
                      icon={<GroupsIcon sx={{ fontSize: '0.95rem !important' }} />}
                      label={booking.group_booking_code}
                      component={Link}
                      to={`/booking/group/detail/${booking.group_booking_id}/`}
                      clickable
                      sx={{
                        mb: 0.75,
                        fontWeight: 700,
                        bgcolor: alpha(accent, isDark ? 0.18 : 0.12),
                        color: isDark ? '#e2e8f0' : '#042018',
                        border: `1px solid ${alpha(accent, 0.35)}`,
                        '& .MuiChip-icon': { color: accent },
                      }}
                    />
                  ) : null}
                  <Stack spacing={0.75}>
                    <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={0.75}>
                      <Typography
                        variant="body2"
                        sx={{ color: isDark ? '#cbd5e1' : '#334155', fontWeight: 700 }}
                      >
                        {scheduledCheckInLabel}
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#64748b' : '#94a3b8' }}>
                        →
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: isDark ? '#cbd5e1' : '#334155', fontWeight: 700 }}
                      >
                        {scheduledCheckOutLabel}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}
                    >
                      {stayDurationMeta.summaryText}
                      {' · '}
                      {booking.total_rooms || booking.rooms_count || 0} room
                      {(booking.total_rooms || booking.rooms_count) !== 1 ? 's' : ''}
                    </Typography>
                  </Stack>
                  {!isCompact && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Chip
                        label={formatStatusLabel(booking.status)}
                        color={getStatusColor(booking.status)}
                        size="small"
                        sx={{ fontWeight: 700, textTransform: 'capitalize', flexShrink: 0 }}
                      />
                      {sameDayStay ? (
                        <Chip
                          label="Hourly"
                          size="small"
                          sx={{
                            fontWeight: 700,
                            height: 24,
                            backgroundColor: alpha('#ef6c00', isDark ? 0.22 : 0.12),
                            color: isDark ? '#ffb74d' : '#ef6c00',
                            border: `1px solid ${alpha('#ef6c00', 0.35)}`,
                          }}
                        />
                      ) : null}
                    </Box>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 1,
                  width: { xs: '100%', sm: 'auto' },
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    textAlign: 'center',
                    border: `1px solid ${alpha(accent, isDark ? 0.28 : 0.22)}`,
                    bgcolor: isDark ? alpha(accent, 0.08) : alpha(accent, 0.05),
                  }}
                >
                  <Typography variant={isCompact ? 'h6' : 'h5'} sx={{ fontWeight: 800, color: accent, lineHeight: 1.1 }}>
                    {formatCurrency(computedTotal, booking.currency)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
                    Total
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    textAlign: 'center',
                    border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
                    bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#0f172a', 0.02),
                  }}
                >
                  <Typography
                    variant={isCompact ? 'h6' : 'h5'}
                    sx={{ fontWeight: 800, color: isDark ? '#e2e8f0' : '#0f172a', lineHeight: 1.1 }}
                  >
                    {booking.total_rooms || booking.rooms_count || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
                    Rooms
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Paper>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Left column: Booking summary + Guest Information */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={modernPanelCardSx}
          >
            <CardContent sx={{ p: { xs: 1.6, sm: 1.9 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1.25, sm: 2 }, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon sx={{ color: accent, fontSize: 22 }} />
                  Booking summary
                </Typography>
                {(() => {
                  const editButton = (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                  onClick={() => {
                    if (!normalizedBookingId) {
                      setError('Invalid booking ID');
                      return;
                    }
                    if (!canEditBooking) {
                      setSnackbar({
                        open: true,
                        message: getPermissionDeniedMessage(PERMISSIONS.BOOKING_MODIFY, 'Modify Booking'),
                        severity: 'warning',
                      });
                      return;
                    }
                    navigate(`/booking/edit/${normalizedBookingId}/`);
                  }}
                  disabled={!canEditBooking}
                  sx={{
                    color: accent,
                    borderColor: alpha(accent, 0.52),
                    backgroundColor: alpha(accent, isDark ? 0.1 : 0.06),
                    borderRadius: 1.5,
                    px: 1.2,
                    fontWeight: 700,
                    textTransform: 'none',
                    flexShrink: 0,
                    '&:hover': {
                      borderColor: accent,
                      backgroundColor: alpha(accent, isDark ? 0.2 : 0.12),
                    },
                  }}
                >
                  Edit
                </Button>
                  );
                  return !canEditBooking ? (
                    <Tooltip title={getPermissionDeniedMessage(PERMISSIONS.BOOKING_MODIFY, 'Modify Booking')} arrow>
                      <span>{editButton}</span>
                    </Tooltip>
                  ) : editButton;
                })()}
              </Box>
              {!isCompact && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: 1.25,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <HotelIcon sx={{ fontSize: 20, color: accent }} />
                    <Typography variant="body2" sx={{ color: isDark ? 'rgba(148, 163, 184, 0.92)' : '#134e4a', fontWeight: 600 }}>
                      {stayDurationMeta.summaryText} · {booking.total_rooms || booking.rooms_count || 0} room{(booking.total_rooms || booking.rooms_count) !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              <BookingSummaryDetailRow icon={CheckInIcon} label="Actual check-in" isDark={isDark} accent={accent}>
                {actualCheckInDisplay?.date ? (
                  <>
                    <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#0f172a', fontWeight: 700 }}>
                      {actualCheckInDisplay.date}
                    </Typography>
                    {actualCheckInDisplay.time ? (
                      <Chip
                        label={actualCheckInDisplay.time}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          border: `1px solid ${alpha(accent, 0.35)}`,
                          backgroundColor: alpha(accent, isDark ? 0.14 : 0.1),
                          color: accent,
                        }}
                      />
                    ) : null}
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontWeight: 600 }}>—</Typography>
                )}
              </BookingSummaryDetailRow>
              <BookingSummaryDetailRow icon={CheckOutIcon} label="Actual check-out" isDark={isDark} accent={accent}>
                {actualCheckOutDisplay?.date ? (
                  <>
                    <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#0f172a', fontWeight: 700 }}>
                      {actualCheckOutDisplay.date}
                    </Typography>
                    {actualCheckOutDisplay.time ? (
                      <Chip
                        label={actualCheckOutDisplay.time}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          border: `1px solid ${alpha(accent, 0.35)}`,
                          backgroundColor: alpha(accent, isDark ? 0.14 : 0.1),
                          color: accent,
                        }}
                      />
                    ) : null}
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontWeight: 600 }}>—</Typography>
                )}
              </BookingSummaryDetailRow>
              {booking.created_at_display && (
                <BookingSummaryDetailRow
                  icon={EventIcon}
                  label="Booked on"
                  isDark={isDark}
                  accent={accent}
                  iconColor={isDark ? '#93c5fd' : '#475569'}
                >
                  {(() => {
                    const d = booking.created_at_display;
                    const parts = d && String(d).includes(', ') ? String(d).split(', ') : [d, null];
                    const isMigratedBooking =
                      String(booking.code || '').toUpperCase().startsWith('MG-') ||
                      String(booking.internal_notes || '').includes('Migrated booking');
                    return (
                      <>
                        <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#0f172a', fontWeight: 700 }}>
                          {parts[0]}
                        </Typography>
                        {!isMigratedBooking && parts[1] && (
                          <Chip
                            label={parts[1]}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              border: `1px solid ${isDark ? alpha('#93c5fd', 0.5) : alpha('#2563eb', 0.28)}`,
                              backgroundColor: isDark ? alpha('#1e3a8a', 0.24) : alpha('#dbeafe', 0.9),
                              color: isDark ? '#bfdbfe' : '#1d4ed8',
                            }}
                          />
                        )}
                      </>
                    );
                  })()}
                </BookingSummaryDetailRow>
              )}
              {String(booking.booking_source || '').trim() ? (
                <Box sx={{ mt: 0.5 }}>
                  {(() => {
                    const srcKey = normalizeBookingSourceKey(booking.booking_source);
                    const { Icon, color } = bookingSourceIconAndColor(booking.booking_source);
                    const sourceLabel =
                      srcKey === 'ota'
                        ? BOOKING_SOURCE_LABELS.ota
                        : formatBookingSourceLabel(booking.booking_source);
                    const showOtaChannelImage =
                      srcKey === 'ota'
                      && booking.channel != null
                      && booking.channel.id != null;
                    const otaChannelName =
                      srcKey === 'ota' && booking.channel != null && booking.channel.id != null
                        ? String(booking.channel.name || '').trim() || ''
                        : '';
                    const primarySourceText = otaChannelName || sourceLabel;
                    const SourceIcon = showOtaChannelImage
                      ? null
                      : srcKey === 'ota'
                        ? ConnectingAirportsIcon
                        : Icon;
                    return (
                      <BookingSummaryDetailRow
                        icon={SourceIcon || MonitorIcon}
                        label="Booking source"
                        isDark={isDark}
                        accent={accent}
                        iconColor={srcKey === 'ota' ? 'primary.main' : color}
                      >
                        {showOtaChannelImage ? (
                          <ChannelImage channel={booking.channel} size={22} />
                        ) : null}
                        <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#0f172a', fontWeight: 700 }}>
                          {primarySourceText || '—'}
                        </Typography>
                        {srcKey === 'ota' && otaChannelName ? (
                          <Typography variant="caption" sx={{ color: isDark ? '#9ca3af' : '#6b7280', width: '100%' }}>
                            {sourceLabel}
                          </Typography>
                        ) : null}
                      </BookingSummaryDetailRow>
                    );
                  })()}
                </Box>
              ) : null}
              {String(booking.booking_type || '').trim()
              || (booking.booking_type_display && booking.booking_type_display !== '—') ? (
                <Box>
                  {(() => {
                    const typeLabel = formatBookingTypeLabel(
                      booking.booking_type,
                      booking.booking_type_display
                    );
                    const { Icon, color } = bookingTypeIconAndColor(booking.booking_type);
                    return (
                      <BookingSummaryDetailRow
                        icon={Icon}
                        label="Booking type"
                        isDark={isDark}
                        accent={accent}
                        iconColor={color}
                      >
                        <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#0f172a', fontWeight: 700 }}>
                          {typeLabel}
                        </Typography>
                      </BookingSummaryDetailRow>
                    );
                  })()}
                </Box>
              ) : null}

              {(() => {
                const purposeCode = String(booking.purpose_of_stay || '').trim();
                const purposeNote = purposeCode === 'other'
                  ? String(booking.purpose_of_stay_note || '').trim()
                  : '';
                const purposeLabel = formatPurposeOfStayLabel(
                  booking.purpose_of_stay,
                  booking.purpose_of_stay_display,
                  ''
                );
                if (!purposeLabel && !purposeNote) return null;
                return (
                  <BookingSummaryDetailRow
                    icon={FlagOutlinedIcon}
                    label="Purpose of stay"
                    isDark={isDark}
                    accent={accent}
                    iconColor={isDark ? '#fbbf24' : '#b45309'}
                  >
                    <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#0f172a', fontWeight: 700 }}>
                      {purposeLabel || 'Other'}
                    </Typography>
                    {purposeNote ? (
                      <Typography
                        variant="caption"
                        sx={{ color: isDark ? '#9ca3af' : '#6b7280', width: '100%', whiteSpace: 'pre-line' }}
                      >
                        {purposeNote}
                      </Typography>
                    ) : null}
                  </BookingSummaryDetailRow>
                );
              })()}

              {hasNotesAndRequests ? (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <FactCheckIcon sx={{ fontSize: 18, color: accent }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        letterSpacing: 0.6,
                        textTransform: 'uppercase',
                        color: isDark ? '#999' : '#666',
                      }}
                    >
                      Notes & Requests
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      ...innerPanelSx,
                      overflow: 'hidden',
                    }}
                  >
                    {notesAndRequests.map(({ key, title, value, Icon, color }, idx) => (
                      <Box
                        key={key}
                        sx={{
                          p: 1.1,
                          borderBottom:
                            idx < notesAndRequests.length - 1
                              ? `1px solid ${surfaceTokens.surfaceBorder}`
                              : 'none',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.35 }}>
                          <Icon sx={{ fontSize: 15, color }} />
                          <Typography variant="caption" sx={{ color: isDark ? '#e5e7eb' : '#1f2937', fontWeight: 700 }}>
                            {title}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            mt: 0.2,
                            px: 0.9,
                            py: 0.65,
                            borderRadius: 0.9,
                            borderLeft: `2px solid ${color}`,
                            backgroundColor: isDark ? alpha(color, 0.16) : alpha(color, 0.08),
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDark ? '#e5e7eb' : '#1f2937',
                              whiteSpace: 'pre-line',
                              fontStyle: 'italic',
                              lineHeight: 1.45,
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : null}
              <Divider sx={{ my: 1.8, borderColor: surfaceTokens.surfaceBorder }} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.1,
                  py: 0.9,
                  borderRadius: 1.4,
                  border: `1px solid ${alpha(accent, isDark ? 0.22 : 0.28)}`,
                  backgroundColor: isDark ? alpha(accent, 0.1) : alpha(accent, 0.08),
                }}
              >
                <MoneyIcon sx={{ fontSize: 20, color: accent }} />
                <Typography variant="body2" sx={{ color: isDark ? 'rgba(148, 163, 184, 0.92)' : '#115e59', fontWeight: 700 }}>Total</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: accent }}>{formatCurrency(computedTotal, booking.currency)}</Typography>
              </Box>
              {booking.advance_amount > 0 && (
                <Typography variant="body2" sx={{ color: isDark ? 'rgba(148, 163, 184, 0.92)' : '#0f766e', mt: 0.9, px: 0.4, fontWeight: 600 }}>
                  Advance: {formatCurrency(booking.advance_amount, booking.currency)}
                </Typography>
              )}
              <BookingPaymentLinkPanel
                bookingId={normalizedBookingId}
                booking={booking}
                remainingAmount={paymentSummaryValues.remainingComputed}
                currency={booking.currency}
                isDark={isDark}
                accent={accent}
                onRefresh={refetchBooking}
                onNotice={paymentLinkNotice}
                canSendEmail={canSendPaymentLinkEmail}
                canSendWhatsApp={canSendPaymentLinkWhatsApp}
              />
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? '#aaa' : '#555', mb: 1.5 }}>
                Documents
              </Typography>
              <Box
                sx={{
                  mb: 0.5,
                  p: { xs: 1.2, sm: 1.5 },
                  ...innerPanelSx,
                }}
              >
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ display: 'block', color: isDark ? '#94a3b8' : '#475569', mb: 0.8, fontWeight: 800, letterSpacing: 0.35 }}>
                    Invoice Type
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    fullWidth
                    value={invoiceScope}
                    onChange={(_, value) => {
                      if (!value) return;
                      setInvoiceScope(value);
                    }}
                    sx={{
                      border: `1px solid ${surfaceTokens.surfaceBorder}`,
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: 800,
                        py: 0.85,
                        borderColor: 'transparent',
                        color: isDark ? '#e2e8f0' : '#1e293b',
                        backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : alpha('#f8fafc', 0.96),
                      },
                      '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                        marginLeft: 0,
                        borderLeft: `1px solid ${surfaceTokens.surfaceBorder}`,
                      },
                      '& .Mui-selected': {
                        backgroundColor: `${accent} !important`,
                        color: '#04120e !important',
                        boxShadow: `inset 0 0 0 1px ${alpha(accent, 0.35)}`,
                      },
                    }}
                  >
                    {INVOICE_SCOPE_OPTIONS.map((option) => (
                      <ToggleButton key={`invoice-scope-${option.value}`} value={option.value}>
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>

                <BookingDocumentsPreviewPanel
                  bookingId={normalizedBookingId}
                  bookingCode={booking?.code}
                  invoiceScope={invoiceScope}
                  invoiceScopeLabel={INVOICE_SCOPE_OPTIONS.find((option) => option.value === invoiceScope)?.label || 'Invoice'}
                  invoiceNumber={bookingInvoiceNumber}
                  guestName={primaryGuestDisplayName}
                  isDark={isDark}
                  accent={accent}
                  docLoading={docLoading}
                  setDocLoading={setDocLoading}
                  canSendEmail={canSendInvoiceEmail}
                  canSendWhatsApp={canSendInvoiceWhatsApp}
                  onNotice={({ message, severity }) => setSnackbar({ open: true, message, severity })}
                />
                    {(canSendReviewEmail || canSendReviewWhatsApp) && (
                      <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        sx={{ mt: 1, textTransform: 'none', fontWeight: 700, borderColor: alpha(accent, 0.52), color: accent }}
                        startIcon={docLoading === 'send_review' ? <CircularProgress size={14} /> : <RateReviewIcon sx={{ fontSize: 18 }} />}
                        disabled={!!docLoading}
                        onClick={async () => {
                          setDocLoading('send_review');
                          try {
                            if (!normalizedBookingId) throw new Error('Invalid booking ID');
                            const res = await sendBookingReviewRequest(normalizedBookingId, {
                              send_email: canSendReviewEmail,
                              send_whatsapp: canSendReviewWhatsApp,
                            });
                            if (res.email_sent || res.whatsapp_sent) {
                              const parts = [];
                              if (res.email_sent) parts.push('Review request sent by email.');
                              if (res.whatsapp_sent) {
                                parts.push(formatWhatsAppSuccessMessage(res, {
                                  prefix: 'WhatsApp review request sent',
                                  bookingCode: booking?.code,
                                }));
                              }
                              const errors = Array.isArray(res.errors) ? res.errors.filter(Boolean) : [];
                              setSnackbar({
                                open: true,
                                message: errors.length ? `${parts.join(' ')} ${errors.join(' ')}` : parts.join(' '),
                                severity: errors.length ? 'warning' : 'success',
                              });
                            } else {
                              setSnackbar({
                                open: true,
                                message: res.whatsapp_user_message || res.errors?.[0] || res.error || 'Review request was not sent',
                                severity: 'warning',
                              });
                            }
                          } catch (e) {
                            setSnackbar({
                              open: true,
                              message: e?.response?.data?.error || e?.message || 'Send failed',
                              severity: 'error',
                            });
                          } finally {
                            setDocLoading(null);
                          }
                        }}
                      >
                        Request review
                      </Button>
                    )}
              </Box>
            </CardContent>
          </Card>

          {/* Guest Information Card with link to customer(s) */}
          {(displayPrimaryGuest || additionalBookingGuests.length > 0) && (
            <Card
              elevation={0}
              sx={modernPanelCardSx}
            >
              <CardContent sx={{ p: { xs: 1.6, sm: 1.9 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <PersonIcon sx={{ color: accent, fontSize: 22 }} />
                    Guest Information
                    {additionalBookingGuests.length > 0 && (
                      <Chip
                        label={`+${additionalBookingGuests.length}`}
                        size="small"
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          border: `1px solid ${isDark ? alpha(accent, 0.45) : alpha(accent, 0.35)}`,
                          backgroundColor: isDark ? alpha(accent, 0.2) : alpha(accent, 0.14),
                          color: isDark ? accent : '#115e59',
                        }}
                      />
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {displayPrimaryGuest && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          stayGuestDrawer.openStayGuest({
                            stayGuest: displayPrimaryGuest,
                            bookingId: booking?.id,
                            bookingCode: booking?.code,
                          });
                        }}
                        onMouseEnter={() =>
                          stayGuestDrawer.prefetchStayGuestDocs(displayPrimaryGuest, booking?.id)
                        }
                        sx={{
                          color: '#042f2e',
                          backgroundColor: accent,
                          borderRadius: 1.5,
                          px: 1.2,
                          fontWeight: 700,
                          textTransform: 'none',
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: accent,
                            filter: 'brightness(0.97)',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        View stay guest
                      </Button>
                    )}
                    {displayPrimaryGuest?.id != null && (
                      <Link
                        to={`/customerhub/customers/${displayPrimaryGuest.id}/`}
                        style={{ textDecoration: 'none' }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            color: accent,
                            borderColor: alpha(accent, 0.52),
                            backgroundColor: alpha(accent, isDark ? 0.1 : 0.06),
                            borderRadius: 1.5,
                            px: 1.2,
                            fontWeight: 700,
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: accent,
                              backgroundColor: alpha(accent, isDark ? 0.2 : 0.12),
                            },
                          }}
                        >
                          View customer
                        </Button>
                      </Link>
                    )}
                  </Box>
                </Box>
                {displayPrimaryGuest && (
                  <>
                    <Typography variant="caption" sx={{ display: 'block', color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Primary guest
                    </Typography>
                    <Box
                      sx={{
                        p: 1.15,
                        borderRadius: 1.5,
                        border: `1px solid ${isDark ? alpha('#64748b', 0.28) : alpha('#94a3b8', 0.26)}`,
                        backgroundColor: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#f8fafc', 0.92),
                        mb: 1.6,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: (displayPrimaryGuest.email || displayPrimaryGuest.phone) ? 1 : 0 }}>
                        <PersonIcon sx={{ fontSize: 19, color: accent }} />
                        <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#0f172a', fontWeight: 700 }}>
                          {primaryGuestDisplayName}
                        </Typography>
                      </Box>
                      {displayPrimaryGuest.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: displayPrimaryGuest.phone ? 1 : 0 }}>
                          <EmailIcon sx={{ fontSize: 18, color: isDark ? '#93c5fd' : '#2563eb' }} />
                          <Typography variant="body2" sx={{ color: isDark ? '#cbd5e1' : '#334155' }}>{displayPrimaryGuest.email}</Typography>
                        </Box>
                      )}
                      {displayPrimaryGuest.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <PhoneIcon sx={{ fontSize: 18, color: isDark ? '#93c5fd' : '#2563eb' }} />
                          <Typography variant="body2" sx={{ color: isDark ? '#cbd5e1' : '#334155' }}>{displayPrimaryGuest.phone}</Typography>
                        </Box>
                      )}
                    </Box>
                  </>
                )}
                {additionalBookingGuests.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.8, borderColor: isDark ? alpha('#64748b', 0.28) : alpha('#94a3b8', 0.26) }} />
                    <Typography variant="caption" sx={{ display: 'block', color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, mb: 1.2, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Additional guests
                    </Typography>
                    {additionalBookingGuests.map((g, idx) => (
                      <Box
                        key={g.id}
                        sx={{
                          mb: idx < additionalBookingGuests.length - 1 ? 1 : 0,
                          p: 1.1,
                          borderRadius: 1.3,
                          border: `1px solid ${isDark ? alpha('#64748b', 0.26) : alpha('#94a3b8', 0.23)}`,
                          backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : alpha('#f8fafc', 0.85),
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                          <Chip
                            label={`+${idx + 1}`}
                            size="small"
                            sx={{
                              height: 22,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              border: `1px solid ${isDark ? alpha(accent, 0.45) : alpha(accent, 0.35)}`,
                              backgroundColor: isDark ? alpha(accent, 0.2) : alpha(accent, 0.14),
                              color: isDark ? accent : '#115e59',
                            }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => {
                                stayGuestDrawer.openStayGuest({
                                  stayGuest: g,
                                  bookingId: booking?.id,
                                  bookingCode: booking?.code,
                                });
                              }}
                              onMouseEnter={() => stayGuestDrawer.prefetchStayGuestDocs(g, booking?.id)}
                              sx={{
                                color: '#042f2e',
                                backgroundColor: accent,
                                fontWeight: 700,
                                textTransform: 'none',
                                minWidth: 0,
                                borderRadius: 1.3,
                                px: 0.8,
                                boxShadow: 'none',
                                '&:hover': {
                                  backgroundColor: accent,
                                  filter: 'brightness(0.97)',
                                  boxShadow: 'none',
                                },
                              }}
                            >
                              Stay guest
                            </Button>
                            {g.id != null && (
                              <Link to={`/customerhub/customers/${g.id}/`} style={{ textDecoration: 'none' }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                                  sx={{
                                    color: accent,
                                    borderColor: alpha(accent, 0.52),
                                    backgroundColor: alpha(accent, isDark ? 0.1 : 0.06),
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    minWidth: 0,
                                    borderRadius: 1.3,
                                    px: 0.8,
                                    '&:hover': {
                                      borderColor: accent,
                                      backgroundColor: alpha(accent, isDark ? 0.2 : 0.12),
                                    },
                                  }}
                                >
                                  Customer
                                </Button>
                              </Link>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: g.phone ? 0.9 : 0 }}>
                          <PersonIcon sx={{ fontSize: 18, color: accent }} />
                          <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#0f172a', fontWeight: 700 }}>
                            {formatBookingGuestName(g)}
                          </Typography>
                        </Box>
                        {g.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <PhoneIcon sx={{ fontSize: 18, color: isDark ? '#93c5fd' : '#2563eb' }} />
                            <Typography variant="body2" sx={{ color: isDark ? '#cbd5e1' : '#334155' }}>{g.phone}</Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right column: Booking Items + Payment Summary */}
        <Grid item xs={12} md={8}>
          <Card
            elevation={0}
            sx={modernPanelCardSx}
          >
            <Box sx={modernPanelHeaderSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReceiptIcon sx={{ fontSize: 22, color: accent }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a' }}>
                  Booking Items
                </Typography>
              </Box>
            </Box>
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#f1f5f9', 0.95) }}>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Room Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Room</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Guest</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Rate Plan</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }} align="right">Adults</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }} align="right">Children</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }} align="right">Price</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }} align="right">GST</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }} align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bookingItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No items found
                        </TableCell>
                      </TableRow>
                    ) : (
                      bookingItems.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {formatDate(item.date)}
                          </TableCell>
                          <TableCell sx={{ color: 'text.primary' }}>
                            {item.room_type?.name || '-'}
                          </TableCell>
                          <TableCell sx={{ color: 'text.primary' }}>
                            {item.allocated_room ? (
                              <Chip
                                label={String(item.allocated_room.name || item.allocated_room.number || 'Allocated').replace(/^Room\s+/i, '')}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 24,
                                  borderRadius: 1.2,
                                  fontSize: '0.72rem',
                                  fontWeight: 800,
                                  borderColor: isDark ? alpha('#14b8a6', 0.46) : alpha('#14b8a6', 0.38),
                                  backgroundColor: isDark ? alpha('#14b8a6', 0.12) : alpha('#14b8a6', 0.08),
                                  color: isDark ? '#99f6e4' : '#0f766e',
                                  '& .MuiChip-label': {
                                    px: 0.9,
                                  },
                                }}
                              />
                            ) : (
                              <Chip
                                label="Pending"
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 24,
                                  borderRadius: 1.2,
                                  fontSize: '0.72rem',
                                  fontWeight: 800,
                                  borderColor: isDark ? alpha('#f59e0b', 0.48) : alpha('#f59e0b', 0.42),
                                  backgroundColor: isDark ? alpha('#f59e0b', 0.12) : alpha('#f59e0b', 0.08),
                                  color: isDark ? '#fde68a' : '#b45309',
                                  '& .MuiChip-label': {
                                    px: 0.9,
                                  },
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: 'text.primary',
                              maxWidth: 220,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                            }}
                            title={formatBookingItemGuestNames(item)}
                          >
                            {formatBookingItemGuestNames(item)}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {item.rate_plan?.name || '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary' }}>
                            {item.adults || 0}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary' }}>
                            {item.children || 0}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary' }}>
                            {formatCurrency(item.price || 0, booking.currency)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary' }}>
                            <Box>
                              {formatCurrency(item.gst_amount ?? item.taxes ?? 0, booking.currency)}
                              {Number(item.gst_percentage || 0) > 0 && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', opacity: 0.82 }}>
                                  ({Number(item.gst_percentage)}%)
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.primary', fontWeight: 500 }}>
                            {formatCurrency(
                              item.total ?? ((item.price || 0) + (item.gst_amount ?? item.taxes ?? 0) + (item.fees || 0)),
                              booking.currency
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {restaurantOrders.hasOrders && (
            <Card
              elevation={0}
              sx={modernPanelCardSx}
            >
              <Box
                sx={{
                  ...modernPanelHeaderSx,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.25,
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RestaurantIcon sx={{ fontSize: 22, color: accent }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a' }}>
                    Restaurant Orders
                  </Typography>
                  <Chip
                    size="small"
                    label={`${restaurantOrders.orders.length} order${restaurantOrders.orders.length !== 1 ? 's' : ''}`}
                    sx={{
                      height: 22,
                      fontSize: '0.68rem',
                      backgroundColor: isDark ? alpha(accent, 0.2) : alpha(accent, 0.12),
                      color: accent,
                      fontWeight: 700,
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, ml: { sm: 'auto' } }}>
                  {restaurantOrders.orders.map((order) => (
                    <Chip
                      key={order.id}
                      size="small"
                      label={`${order.order_number || `#${order.id}`} · ${formatCurrency(order.total_amount || 0, booking.currency)}`}
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 800,
                        backgroundColor: isDark ? alpha('#14b8a6', 0.18) : alpha('#14b8a6', 0.1),
                        color: isDark ? '#99f6e4' : '#0f766e',
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <CardContent sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#f1f5f9', 0.95) }}>
                        <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Order</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Room</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Item</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }} align="right">Qty</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }} align="right">Rate</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }} align="right">GST</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#333', borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }} align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {restaurantOrders.items.map((item) => {
                        const order = restaurantOrderById.get(item.order_id);
                        const roomDisplay = String(order?.location_label || '-').replace(/^Room\s+/i, '');
                        return (
                          <TableRow key={`${item.order_id}-${item.id}`} hover>
                            <TableCell sx={{ color: 'text.secondary', py: 0.9 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                {item.order_number || '-'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                                {formatDateTime(item.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ color: 'text.primary', py: 0.9 }}>
                              <Chip
                                size="small"
                                label={roomDisplay}
                                variant="outlined"
                                sx={{
                                  height: 24,
                                  borderRadius: 1.2,
                                  fontSize: '0.72rem',
                                  fontWeight: 800,
                                  borderColor: isDark ? alpha('#14b8a6', 0.46) : alpha('#14b8a6', 0.38),
                                  backgroundColor: isDark ? alpha('#14b8a6', 0.12) : alpha('#14b8a6', 0.08),
                                  color: isDark ? '#99f6e4' : '#0f766e',
                                  '& .MuiChip-label': {
                                    px: 0.9,
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: 'text.primary', py: 0.9 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                {item.item_name || 'Item'}
                              </Typography>
                              {item.variation_name && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                                  {item.variation_name}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary', py: 0.9 }}>
                              {Number(item.quantity || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary', py: 0.9 }}>
                              {formatCurrency(item.unit_price || 0, booking.currency)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary', py: 0.9 }}>
                              <Box>
                                {formatCurrency(item.gst_amount || 0, booking.currency)}
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', opacity: 0.82 }}>
                                  ({item.gst_rate_display || '0%'})
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'text.primary', fontWeight: 700, py: 0.9 }}>
                              {formatCurrency(item.total_with_gst ?? item.line_total ?? 0, booking.currency)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Booking Audit — Owner / GM / Manager or Additional Access only */}
          {canViewBookingAudit && (
          <Card
            elevation={0}
            sx={modernPanelCardSx}
          >
            <Box
              onClick={() => setAuditExpanded((prev) => !prev)}
              sx={{
                ...modernPanelHeaderSx,
                borderBottom: auditExpanded
                  ? `1px solid ${surfaceTokens.surfaceBorder}`
                  : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FactCheckIcon sx={{ fontSize: 22, color: accent }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a' }}>
                  Booking Audit
                </Typography>
                {auditLoaded && (
                  <Chip
                    size="small"
                    label={`${auditEvents.length} event${auditEvents.length !== 1 ? 's' : ''}`}
                    sx={{
                      height: 22,
                      fontSize: '0.68rem',
                      backgroundColor: isDark ? alpha(accent, 0.2) : alpha(accent, 0.12),
                      color: accent,
                    }}
                  />
                )}
              </Box>
              {auditExpanded ? (
                <KeyboardArrowUpIcon sx={{ color: isDark ? '#bbb' : '#666' }} />
              ) : (
                <KeyboardArrowDownIcon sx={{ color: isDark ? '#bbb' : '#666' }} />
              )}
            </Box>
            {auditExpanded && (
              <CardContent sx={{ p: 1.8 }}>
                <Box sx={{ mb: 1.5 }}>
                  <Button
                    variant="outlined"
                    onClick={fetchBookingAuditEventsData}
                    disabled={auditLoading || !canViewBookingAudit}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      color: accent,
                      borderColor: alpha(accent, 0.5),
                      backgroundColor: alpha(accent, isDark ? 0.08 : 0.05),
                      borderRadius: 1.3,
                      px: 1.1,
                      py: 0.45,
                      '&:hover': {
                        borderColor: accent,
                        backgroundColor: alpha(accent, isDark ? 0.2 : 0.12),
                      },
                    }}
                  >
                    {auditLoading ? 'Refreshing timeline...' : 'Refresh timeline'}
                  </Button>
                </Box>
                {auditLoading && (
                  <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
                {!auditLoading && auditError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {auditError}
                  </Alert>
                )}
                {!auditLoading && !auditError && auditEvents.length === 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderRadius: 2,
                      border: `1px dashed ${isDark ? alpha('#64748b', 0.45) : alpha('#94a3b8', 0.45)}`,
                      backgroundColor: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#f8fafc', 0.9),
                    }}
                  >
                    <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666' }}>
                      No booking activity found yet.
                    </Typography>
                  </Paper>
                )}
                {!auditLoading && !auditError && auditEvents.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {auditEvents.map((event, index) => {
                      const meta = getAuditEventMeta(event.event_type);
                      const actorUser = event?.payload?.actor_user || null;
                      const actorLabel = sanitizeBookingAuditActor(
                        actorUser?.full_name || actorUser?.username || actorUser?.email || event.created_by || 'system'
                      );
                      return (
                        <Paper
                          key={event.id || `${event.event_type}-${index}`}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${isDark ? alpha('#64748b', 0.35) : alpha('#cbd5e1', 0.55)}`,
                            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : alpha('#ffffff', 0.94),
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box
                                sx={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: meta.color,
                                  border: `1px solid ${alpha(meta.color, 0.5)}`,
                                  backgroundColor: isDark ? alpha('#fff', 0.04) : '#fff',
                                }}
                              >
                                {meta.icon}
                              </Box>
                              <Typography variant="subtitle2" sx={{ color: isDark ? '#f0f0f5' : '#20232a', fontWeight: 700 }}>
                                {event.title || 'Booking Activity'}
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666' }}>
                              {event.created_at_display && String(event.created_at_display).trim()
                                ? event.created_at_display
                                : formatDateTime(event.created_at)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: isDark ? '#d7d7df' : '#454c57' }}>
                            {sanitizeBookingAuditText(event.description) || 'Booking activity captured.'}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              label={`By ${actorLabel}`}
                              sx={{
                                height: 22,
                                fontSize: '0.68rem',
                                backgroundColor: isDark ? alpha(accent, 0.2) : alpha(accent, 0.12),
                                color: accent,
                              }}
                            />
                            <Chip
                              size="small"
                              label={(event.event_type || 'activity').replace(/_/g, ' ')}
                              sx={{
                                height: 22,
                                fontSize: '0.68rem',
                                textTransform: 'capitalize',
                                backgroundColor: isDark ? alpha('#9c27b0', 0.22) : alpha('#9c27b0', 0.1),
                                color: isDark ? '#ce93d8' : '#6a1b9a',
                              }}
                            />
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            )}
          </Card>
          )}

          {canViewPosAudit && restaurantOrders.hasOrders && (
            <Card
              elevation={0}
              sx={modernPanelCardSx}
            >
              <Box
                onClick={() => setRestaurantAuditExpanded((prev) => !prev)}
                sx={{
                  ...modernPanelHeaderSx,
                  borderBottom: restaurantAuditExpanded
                    ? `1px solid ${surfaceTokens.surfaceBorder}`
                    : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RestaurantIcon sx={{ fontSize: 22, color: '#14b8a6' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a' }}>
                    Restaurant Order Audit
                  </Typography>
                  <Chip
                    size="small"
                    label={`${restaurantOrders.auditEvents.length} event${restaurantOrders.auditEvents.length !== 1 ? 's' : ''}`}
                    sx={{
                      height: 22,
                      fontSize: '0.68rem',
                      backgroundColor: isDark ? alpha('#14b8a6', 0.2) : alpha('#14b8a6', 0.12),
                      color: isDark ? '#99f6e4' : '#0f766e',
                      fontWeight: 700,
                    }}
                  />
                </Box>
                {restaurantAuditExpanded ? (
                  <KeyboardArrowUpIcon sx={{ color: isDark ? '#bbb' : '#666' }} />
                ) : (
                  <KeyboardArrowDownIcon sx={{ color: isDark ? '#bbb' : '#666' }} />
                )}
              </Box>
              {restaurantAuditExpanded && (
                <CardContent sx={{ p: 1.8 }}>
                  {restaurantOrders.auditEvents.length === 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        borderRadius: 2,
                        border: `1px dashed ${isDark ? alpha('#14b8a6', 0.38) : alpha('#14b8a6', 0.35)}`,
                        backgroundColor: isDark ? alpha('#14b8a6', 0.08) : alpha('#14b8a6', 0.05),
                      }}
                    >
                      <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666' }}>
                        No restaurant order activity found yet.
                      </Typography>
                    </Paper>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {restaurantOrders.auditEvents.map((event, index) => {
                        const hasStatusTransition = Boolean(
                          event?.status_from
                          && event?.status_to
                          && String(event.status_from) !== String(event.status_to)
                        );
                        return (
                          <Paper
                            key={event.id || `${event.order_number}-${event.event_type}-${index}`}
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: `1px solid ${isDark ? alpha('#14b8a6', 0.28) : alpha('#14b8a6', 0.22)}`,
                              backgroundColor: isDark ? alpha('#14b8a6', 0.08) : alpha('#ffffff', 0.94),
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box
                                  sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#14b8a6',
                                    border: `1px solid ${alpha('#14b8a6', 0.5)}`,
                                    backgroundColor: isDark ? alpha('#14b8a6', 0.12) : alpha('#14b8a6', 0.08),
                                  }}
                                >
                                  <RestaurantIcon sx={{ fontSize: 14 }} />
                                </Box>
                                <Typography variant="subtitle2" sx={{ color: isDark ? '#f0f0f5' : '#20232a', fontWeight: 700 }}>
                                  {event.title || 'Restaurant Order Activity'}
                                </Typography>
                              </Box>
                              <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666' }}>
                                {event.created_at_display && String(event.created_at_display).trim()
                                  ? event.created_at_display
                                  : formatDateTime(event.created_at)}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: isDark ? '#d7d7df' : '#454c57' }}>
                              {event.description || 'Restaurant order activity captured.'}
                            </Typography>
                            <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                              <Chip
                                size="small"
                                label={`Order ${event.order_number || '-'}`}
                                sx={{
                                  height: 22,
                                  fontSize: '0.68rem',
                                  backgroundColor: isDark ? alpha('#14b8a6', 0.2) : alpha('#14b8a6', 0.12),
                                  color: isDark ? '#99f6e4' : '#0f766e',
                                  fontWeight: 700,
                                }}
                              />
                              <Chip
                                size="small"
                                label={`By ${event.actor_display || 'System'}`}
                                sx={{
                                  height: 22,
                                  fontSize: '0.68rem',
                                  backgroundColor: isDark ? alpha(accent, 0.2) : alpha(accent, 0.12),
                                  color: accent,
                                  fontWeight: 700,
                                }}
                              />
                              {hasStatusTransition && (
                                <Chip
                                  size="small"
                                  label={`${String(event.status_from).replace('_', ' ')} -> ${String(event.status_to).replace('_', ' ')}`}
                                  sx={{
                                    height: 22,
                                    fontSize: '0.68rem',
                                    textTransform: 'capitalize',
                                    backgroundColor: isDark ? alpha('#8b5cf6', 0.22) : alpha('#8b5cf6', 0.12),
                                    color: isDark ? '#ddd6fe' : '#6d28d9',
                                    fontWeight: 700,
                                  }}
                                />
                              )}
                              <Chip
                                size="small"
                                label={(event.event_type || 'activity').replace(/_/g, ' ')}
                                sx={{
                                  height: 22,
                                  fontSize: '0.68rem',
                                  textTransform: 'capitalize',
                                  backgroundColor: isDark ? alpha('#9c27b0', 0.22) : alpha('#9c27b0', 0.1),
                                  color: isDark ? '#ce93d8' : '#6a1b9a',
                                  fontWeight: 700,
                                }}
                              />
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* Payment Summary */}
          <Card
            elevation={0}
            sx={{
              ...modernPanelCardSx,
              mb: 0,
            }}
          >
            <Box sx={modernPanelHeaderSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaymentIcon sx={{ fontSize: 22, color: accent }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a' }}>
                  Payment Summary
                </Typography>
              </Box>
            </Box>
            <CardContent sx={{ p: 1.8 }}>
              <Box sx={{ mb: 2 }}>
                <Divider sx={{ mb: 1.8, borderColor: surfaceTokens.surfaceBorder }} />
                {(() => {
                  const {
                    subtotalBefore,
                    discountPct,
                    discountAmount,
                    subtotalAfter,
                    gstPct,
                    gstAmount,
                    totalDisplay,
                    totalPaid,
                    remainingComputed,
                    afterDiscountLabel,
                    gstPrefix,
                    inclusiveDisplay,
                    extraChargesAmount,
                    extraChargesBaseAmount,
                    extraChargesGstAmount,
                    extraChargesNote,
                    extraChargesItems,
                  } = paymentSummaryValues;
                  return (
                    <Grid container spacing={1.25} alignItems="stretch">
                      <Grid item xs={12} md={restaurantOrders.hasOrders ? 4 : 12}>
                        <Paper elevation={0} sx={financePanelSx(accent)}>
                          <Typography variant="caption" sx={financeSectionTitleSx(accent)}>
                            <HotelIcon sx={{ fontSize: 15 }} />
                            Hotel
                          </Typography>
                          {subtotalBefore > 0 && (
                            <Box sx={financeRowSx}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {inclusiveDisplay ? 'Subtotal' : 'Before Discount'}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(subtotalBefore, booking.currency)}</Typography>
                            </Box>
                          )}
                          {(discountAmount > 0 || discountPct > 0) && (
                            <Box sx={financeRowSx}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Discount {discountPct > 0 ? `(${discountPct}%)` : ''}</Typography>
                              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>− {formatCurrency(discountAmount, booking.currency)}</Typography>
                            </Box>
                          )}
                          {(subtotalAfter > 0 || discountAmount > 0) && (
                            <Box sx={financeRowSx}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{afterDiscountLabel}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(subtotalAfter, booking.currency)}</Typography>
                            </Box>
                          )}
                          {(gstAmount > 0 || gstPct > 0) && (
                            <Box sx={financeRowSx}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                GST {gstPct > 0 ? `(${gstPct}%)` : ''} {inclusiveDisplay ? '(Inclusive)' : booking.gst_included ? '' : ''}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 800 }}>
                                {gstPrefix}{formatCurrency(gstAmount, booking.currency)}
                              </Typography>
                            </Box>
                          )}
                          {extraChargesAmount > 0 ? (
                            <BookingExtraChargesBreakdown
                              items={extraChargesItems}
                              amount={extraChargesAmount}
                              baseAmount={extraChargesBaseAmount}
                              gstAmount={extraChargesGstAmount}
                              note={extraChargesNote}
                              currency={booking.currency}
                              isDark={isDark}
                              accent={accent}
                            />
                          ) : null}
                          <Divider sx={{ my: 1, borderColor: surfaceTokens.surfaceBorder }} />
                          <Box sx={{ ...financeRowSx, mb: 0.75 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: 'text.primary' }}>{restaurantOrders.hasOrders ? 'Hotel Total' : 'Total Amount'}</Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: 'text.primary' }}>{formatCurrency(totalDisplay, booking.currency)}</Typography>
                          </Box>
                          {!restaurantOrders.hasOrders && booking.advance_amount > 0 && (
                            <Box sx={financeRowSx}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Advance Paid</Typography>
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 800 }}>{formatCurrency(booking.advance_amount || 0, booking.currency)}</Typography>
                            </Box>
                          )}
                          {booking.payment_summary && (
                            <>
                              <Box sx={financeRowSx}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{restaurantOrders.hasOrders ? 'Hotel Paid' : 'Paid Amount'}</Typography>
                                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 800 }}>{formatCurrency(totalPaid, booking.currency)}</Typography>
                              </Box>
                              <Box sx={{ ...financeRowSx, mb: 0 }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{restaurantOrders.hasOrders ? 'Hotel Due' : 'Remaining Amount'}</Typography>
                                <Typography variant="body2" sx={{ color: remainingComputed > 0 ? 'error.main' : 'success.main', fontWeight: 900 }}>{formatCurrency(remainingComputed, booking.currency)}</Typography>
                              </Box>
                            </>
                          )}
                        </Paper>
                      </Grid>
                      {restaurantOrders.hasOrders && (
                        <Grid item xs={12} md={4}>
                          <Paper elevation={0} sx={financePanelSx('#14b8a6')}>
                            <Typography variant="caption" sx={financeSectionTitleSx('#14b8a6')}>
                              <RestaurantIcon sx={{ fontSize: 15 }} />
                              Restaurant
                            </Typography>
                            <Box sx={financeRowSx}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Subtotal</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(restaurantOrders.summary.subtotal || 0, booking.currency)}</Typography>
                            </Box>
                            <Box sx={financeRowSx}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Tax</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(restaurantOrders.summary.tax_amount || 0, booking.currency)}</Typography>
                            </Box>
                            {(restaurantOrders.summary.discount_amount || 0) > 0 && (
                              <Box sx={financeRowSx}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Discount</Typography>
                                <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>− {formatCurrency(restaurantOrders.summary.discount_amount || 0, booking.currency)}</Typography>
                              </Box>
                            )}
                            {(restaurantOrders.summary.additional_charges || 0) > 0 && (
                              <Box sx={financeRowSx}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Additional Charges</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(restaurantOrders.summary.additional_charges || 0, booking.currency)}</Typography>
                              </Box>
                            )}
                            <Divider sx={{ my: 1, borderColor: surfaceTokens.surfaceBorder }} />
                            <Box sx={financeRowSx}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Restaurant Total</Typography>
                              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>{formatCurrency(combinedPaymentSummary.restaurantTotal, booking.currency)}</Typography>
                            </Box>
                            <Box sx={financeRowSx}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Restaurant Paid</Typography>
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 800 }}>{formatCurrency(combinedPaymentSummary.restaurantPaid, booking.currency)}</Typography>
                            </Box>
                            <Box sx={{ ...financeRowSx, mb: 0 }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Restaurant Due</Typography>
                              <Typography variant="body2" sx={{ color: combinedPaymentSummary.restaurantRemaining > 0 ? 'error.main' : 'success.main', fontWeight: 900 }}>{formatCurrency(combinedPaymentSummary.restaurantRemaining, booking.currency)}</Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      )}
                      {restaurantOrders.hasOrders && (
                        <Grid item xs={12} md={4}>
                          <Paper elevation={0} sx={financePanelSx('#8b5cf6', true)}>
                            <Typography variant="caption" sx={financeSectionTitleSx('#8b5cf6')}>
                              <PaymentIcon sx={{ fontSize: 15 }} />
                              Combined
                            </Typography>
                            <Box sx={financeRowSx}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Hotel Total</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatCurrency(totalDisplay, booking.currency)}</Typography>
                            </Box>
                            <Box sx={financeRowSx}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Restaurant Total</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatCurrency(combinedPaymentSummary.restaurantTotal, booking.currency)}</Typography>
                            </Box>
                            <Divider sx={{ my: 1, borderColor: isDark ? alpha('#8b5cf6', 0.3) : alpha('#8b5cf6', 0.18) }} />
                            <Box sx={financeRowSx}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Grand Total</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 950, color: '#8b5cf6' }}>{formatCurrency(combinedPaymentSummary.combinedTotal, booking.currency)}</Typography>
                            </Box>
                            <Box sx={financeRowSx}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Total Paid</Typography>
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 900 }}>{formatCurrency(combinedPaymentSummary.combinedPaid, booking.currency)}</Typography>
                            </Box>
                            <Box sx={{ ...financeRowSx, mb: 0 }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Total Due</Typography>
                              <Typography variant="body2" sx={{ color: combinedPaymentSummary.combinedRemaining > 0 ? 'error.main' : 'success.main', fontWeight: 950 }}>{formatCurrency(combinedPaymentSummary.combinedRemaining, booking.currency)}</Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  );
                })()}

                <Box sx={{ mt: 2 }}>
                  <BookingOverpaidBanner
                    returnAmount={paymentSummaryValues.returnAmountComputed}
                    paidAmount={paymentSummaryValues.totalPaid}
                    totalAmount={paymentSummaryValues.totalDisplay}
                    currency={booking.currency}
                    isDark={isDark}
                    detailLabel={restaurantOrders.hasOrders ? 'hotel total' : 'booking total'}
                  />
                </Box>

                {/* Payment Status */}
                {booking.payment_summary && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Payment Status
                    </Typography>
                    <Chip
                      label={effectivePaymentStatus === 'paid' ? 'Paid' : effectivePaymentStatus === 'partial' ? 'Partial' : 'Unpaid'}
                      color={effectivePaymentStatus === 'paid' ? 'success' : effectivePaymentStatus === 'partial' ? 'warning' : 'error'}
                      size="small"
                    />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={SlideLeftTransition}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <CustomerContactInfoDrawer
        open={stayGuestDrawer.open}
        onClose={() => {
          if (!stayGuestSaving) stayGuestDrawer.close();
        }}
        customer={stayGuestDrawer.guest}
        initialMode="view"
        allowEdit
        titleOverride={stayGuestDrawer.titleOverride}
        subtitleOverride={stayGuestDrawer.subtitleOverride}
        bookingId={stayGuestDrawer.bookingId || booking?.id || null}
        onSave={saveStayGuestFromDrawer}
        saving={stayGuestSaving}
      />
    </Box>
  );
};

export default BookingDetailPage;

